import { useRef, useCallback, useState, useEffect } from 'react';
import { monday } from '../service/action';

/**
 * useAutoSave — shared debounced auto-save hook for form editors.
 *
 * Encapsulates: debounce timer, concurrency guard, saving state,
 * toast notifications, and cleanup on unmount.
 *
 * @param {object}  options
 * @param {Function} options.saveFn          - async (token, payload) => void
 * @param {Function} options.getSessionToken - async () => string
 * @param {React.MutableRefObject} options.formRef - ref holding latest form state
 * @param {number}  [options.delay=1000]     - debounce delay in ms
 * @returns {{ autoSave: Function, formSaving: boolean }}
 */
const useAutoSave = ({ saveFn, getSessionToken, formRef, delay = 1000 }) => {
  const timerRef = useRef(null);
  const [formSaving, setFormSaving] = useState(false);
  const formSavingRef = useRef(formSaving);
  formSavingRef.current = formSaving;

  const autoSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      if (formSavingRef.current) return;
      try {
        setFormSaving(true);
        const token = await getSessionToken();
        await saveFn(token, formRef.current);
        monday.execute('notice', { message: 'Changes saved', type: 'success', timeout: 2000 });
      } catch (error) {
        console.error('Auto save failed:', error);
        monday.execute('notice', { message: 'Failed to save changes', type: 'error', timeout: 3000 });
      } finally {
        setFormSaving(false);
      }
    }, delay);
  }, [saveFn, getSessionToken, formRef, delay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { autoSave, formSaving };
};

export default useAutoSave;
